import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ROLE_LABEL,
  supabase,
  TIER_LABEL,
  type AppRole,
  type Invitation,
  type OrganisationDetail,
  type PartnerTier,
  type SubscriptionStatus,
} from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { invokeFunction } from '@/lib/invoke'
import { Badge, Button, Card, ErrorNote, PageHeader } from '@/components/ui'
import { InvitationActions } from '@/components/InvitationActions'

const TIERS: PartnerTier[] = ['core', 'supported', 'partner_plus']
const STATUSES: SubscriptionStatus[] = ['pending', 'active', 'grace', 'lapsed', 'cancelled']
const PURCHASER_TYPES: Array<'community' | 'corporate'> = ['community', 'corporate']
const PARTNER_ROLES: AppRole[] = ['org_admin', 'facilitator']

const ORG_COLUMNS =
  'id, name, slug, tier, purchaser_type, subscription_status, subscription_start, renews_at, ' +
  'grace_ends_at, contact_name, contact_email, contact_phone, data_agreement_signed_at, notes, ' +
  'created_at, updated_at'

interface Member {
  id: string
  full_name: string | null
  email: string
  role: AppRole
  is_active: boolean
  last_seen_at: string | null
}

// data_agreement_signed_at is a timestamptz but is recorded as a day. Read it
// back as the NZ calendar date so an evening signing does not show as the
// previous day (Graph/UTC trap, same shape as the device-reset dates).
function toNzDate(ts: string | null): string {
  if (!ts) return ''
  return new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' })
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('en-NZ') : '—'
}

interface FormValues {
  name: string
  slug: string
  tier: PartnerTier
  purchaser_type: 'community' | 'corporate'
  subscription_status: SubscriptionStatus
  subscription_start: string
  renews_at: string
  grace_ends_at: string
  contact_name: string
  contact_email: string
  contact_phone: string
  data_agreement_signed: string
  notes: string
}

function toForm(org: OrganisationDetail): FormValues {
  return {
    name: org.name,
    slug: org.slug,
    tier: org.tier,
    purchaser_type: org.purchaser_type,
    subscription_status: org.subscription_status,
    subscription_start: org.subscription_start ?? '',
    renews_at: org.renews_at ?? '',
    grace_ends_at: org.grace_ends_at ?? '',
    contact_name: org.contact_name ?? '',
    contact_email: org.contact_email ?? '',
    contact_phone: org.contact_phone ?? '',
    data_agreement_signed: toNzDate(org.data_agreement_signed_at),
    notes: org.notes ?? '',
  }
}

/**
 * One partner organisation: its full record, the people in it, and its
 * pending invitations. Writes are ktm_admin only (policy "org: ktm admin
 * writes"); ktm_staff get the same screen read-only.
 */
export function OrganisationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const { isKtmAdmin } = useAuth()

  const { data: org, isLoading, error } = useQuery({
    queryKey: ['organisation', orgId],
    queryFn: async () => {
      const { data, error: selectError } = await supabase
        .from('organisations')
        .select(ORG_COLUMNS)
        .eq('id', orgId!)
        .maybeSingle()
      if (selectError) throw selectError
      return data as OrganisationDetail | null
    },
    enabled: Boolean(orgId),
  })

  if (isLoading) return <p className="text-sm text-inksoft">Loading…</p>
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>
  if (!org) {
    return (
      <>
        <PageHeader title="Organisation not found">
          It may have been deleted. <Link to="/admin/organisations" className="underline">Back to organisations</Link>.
        </PageHeader>
      </>
    )
  }

  return (
    <>
      <p className="mb-2 text-sm">
        <Link to="/admin/organisations" className="text-inksoft underline underline-offset-4 hover:text-ink">
          ← Partner organisations
        </Link>
      </p>
      <PageHeader title={org.name}>
        {isKtmAdmin
          ? 'Everything about this partner in one place. Changes are recorded in the audit log.'
          : 'Read only. Changing a partner needs a Ki Tua o Matariki admin.'}
      </PageHeader>

      {/* key: reset the form when the saved record changes underneath it. */}
      <DetailsCard key={org.updated_at} org={org} canEdit={isKtmAdmin} />
      <MembersCard orgId={org.id} canEdit={isKtmAdmin} />
      <InvitationsCard orgId={org.id} />
      {isKtmAdmin && <DeleteCard org={org} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------
function DetailsCard({ org, canEdit }: { org: OrganisationDetail; canEdit: boolean }) {
  const queryClient = useQueryClient()
  const [values, setValues] = useState<FormValues>(() => toForm(org))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  function update<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }))
    setSaved(false)
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!values.name.trim()) throw new Error('Give the organisation a name.')
      const slug = values.slug.trim()
      if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
        throw new Error('The slug can only use lower-case letters, numbers and single hyphens.')
      }
      const patch: Partial<OrganisationDetail> = {
        name: values.name.trim(),
        slug,
        tier: values.tier,
        purchaser_type: values.purchaser_type,
        subscription_status: values.subscription_status,
        subscription_start: values.subscription_start || null,
        renews_at: values.renews_at || null,
        grace_ends_at: values.grace_ends_at || null,
        contact_name: values.contact_name.trim() || null,
        contact_email: values.contact_email.trim() || null,
        contact_phone: values.contact_phone.trim() || null,
        notes: values.notes.trim() || null,
      }
      // Only touch the timestamp when the day actually changed, so saving
      // other fields never shifts a recorded signing time.
      if (values.data_agreement_signed !== toNzDate(org.data_agreement_signed_at)) {
        patch.data_agreement_signed_at = values.data_agreement_signed
          ? new Date(`${values.data_agreement_signed}T12:00:00+12:00`).toISOString()
          : null
      }
      const { error: updateError } = await supabase
        .from('organisations')
        .update(patch)
        .eq('id', org.id)
      if (updateError) {
        if (updateError.code === '23505') throw new Error('Another organisation already uses that slug.')
        throw updateError
      }
    },
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['organisation', org.id] })
      await queryClient.invalidateQueries({ queryKey: ['organisations'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    save.mutate()
  }

  const input = 'mt-1 w-full rounded-sm border border-line px-3 py-2 disabled:bg-paper'

  return (
    <Card className="mb-6 max-w-3xl">
      <h2 className="text-base text-ink">Details</h2>
      <form onSubmit={submit}>
        <fieldset disabled={!canEdit} className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-ink">Name</span>
            <input value={values.name} onChange={(e) => update('name', e.target.value)} className={input} required />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">URL slug</span>
            <input value={values.slug} onChange={(e) => update('slug', e.target.value)} className={input} required />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Tier</span>
            <select value={values.tier} onChange={(e) => update('tier', e.target.value as PartnerTier)} className={input}>
              {TIERS.map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Purchaser type</span>
            <select
              value={values.purchaser_type}
              onChange={(e) => update('purchaser_type', e.target.value as 'community' | 'corporate')}
              className={input}
            >
              {PURCHASER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Subscription status</span>
            <select
              value={values.subscription_status}
              onChange={(e) => update('subscription_status', e.target.value as SubscriptionStatus)}
              className={input}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Subscription start</span>
            <input type="date" value={values.subscription_start} onChange={(e) => update('subscription_start', e.target.value)} className={input} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Renews</span>
            <input type="date" value={values.renews_at} onChange={(e) => update('renews_at', e.target.value)} className={input} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Grace ends</span>
            <input type="date" value={values.grace_ends_at} onChange={(e) => update('grace_ends_at', e.target.value)} className={input} />
            <span className="mt-1 block text-xs text-inksoft">Set automatically when a renewal lapses into grace.</span>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-ink">Contact name</span>
            <input value={values.contact_name} onChange={(e) => update('contact_name', e.target.value)} className={input} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Contact email</span>
            <input type="email" value={values.contact_email} onChange={(e) => update('contact_email', e.target.value)} className={input} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Contact phone</span>
            <input type="tel" value={values.contact_phone} onChange={(e) => update('contact_phone', e.target.value)} className={input} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Data agreement signed</span>
            <input
              type="date"
              value={values.data_agreement_signed}
              onChange={(e) => update('data_agreement_signed', e.target.value)}
              className={input}
            />
            <span className="mt-1 block text-xs text-inksoft">No evaluation data can be entered until this is recorded (R45).</span>
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-ink">Notes</span>
            <textarea value={values.notes} onChange={(e) => update('notes', e.target.value)} rows={3} className={input} />
          </label>
        </fieldset>

        {canEdit && (
          <div className="mt-4">
            {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
            {saved && <p className="mb-3 text-sm text-pounamu">Saved.</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </form>
      <p className="mt-4 text-xs text-inksoft">
        Created {formatDate(org.created_at)} · last changed {formatDate(org.updated_at)}
      </p>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Pending invitations, shared by the People and Invitations cards (same
// query key, so React Query fetches once).
// ---------------------------------------------------------------------------
function usePendingInvitations(orgId: string) {
  return useQuery({
    queryKey: ['organisation', orgId, 'invitations'],
    queryFn: async () => {
      const { data, error: selectError } = await supabase
        .from('invitations')
        .select('id, email, org_id, role, created_at, expires_at, accepted_at, invited_by')
        .eq('org_id', orgId)
        .is('accepted_at', null)
        .order('created_at', { ascending: false })
      if (selectError) throw selectError
      return data as Invitation[]
    },
  })
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------
function MembersCard({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: allMembers, isLoading } = useQuery({
    queryKey: ['organisation', orgId, 'members'],
    queryFn: async () => {
      const { data, error: selectError } = await supabase
        .from('profiles')
        .select('id, full_name, email, role, is_active, last_seen_at')
        .eq('org_id', orgId)
        .order('full_name')
      if (selectError) throw selectError
      return data as Member[]
    },
  })

  // Auth creates the account (and handle_new_user its profile) when the
  // invitation is SENT, so an invitee has a profile before they accept.
  // They are listed once, under Invitations, until they do.
  const { data: pending } = usePendingInvitations(orgId)
  const pendingEmails = new Set((pending ?? []).map((inv) => inv.email.toLowerCase()))
  const members = allMembers?.filter((m) => !pendingEmails.has(m.email.toLowerCase()))

  const change = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Member> & { org_id?: null } }) => {
      const { error: updateError } = await supabase.from('profiles').update(patch).eq('id', id)
      if (updateError) throw updateError
    },
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['organisation', orgId, 'members'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <Card className="mb-6 max-w-3xl">
      <h2 className="text-base text-ink">People</h2>
      <p className="mt-1 text-sm text-inksoft">
        Deactivating someone cuts their access at once and keeps everything they recorded.
        Removing them from the organisation also detaches the account, so it sees nothing
        anywhere until it is invited again.
      </p>
      {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}
      {isLoading && <p className="mt-3 text-sm text-inksoft">Loading…</p>}
      {members && members.length === 0 && (
        <p className="mt-3 text-sm text-inksoft">Nobody has accepted an invitation to this organisation yet.</p>
      )}
      {members && members.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-inksoft">
              <tr>
                <th className="py-2 pr-4 font-medium">Person</th>
                <th className="py-2 pr-4 font-medium">Role</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Last seen</th>
                {canEdit && <th className="py-2 font-medium text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-line last:border-0">
                  <td className="py-2 pr-4">
                    <div>{m.full_name || m.email}</div>
                    {m.full_name && <div className="text-xs text-inksoft">{m.email}</div>}
                  </td>
                  <td className="py-2 pr-4">
                    {canEdit && PARTNER_ROLES.includes(m.role) ? (
                      <select
                        aria-label={`Role for ${m.email}`}
                        value={m.role}
                        onChange={(e) => change.mutate({ id: m.id, patch: { role: e.target.value as AppRole } })}
                        className="rounded-sm border border-line px-2 py-1"
                      >
                        {PARTNER_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (
                      ROLE_LABEL[m.role]
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <Badge tone={m.is_active ? 'good' : 'neutral'}>{m.is_active ? 'Active' : 'Not active'}</Badge>
                  </td>
                  <td className="py-2 pr-4 text-inksoft">{formatDate(m.last_seen_at)}</td>
                  {canEdit && (
                    <td className="space-x-3 py-2 text-right whitespace-nowrap">
                      <Button
                        variant="quiet"
                        className="px-0"
                        disabled={change.isPending}
                        onClick={() => change.mutate({ id: m.id, patch: { is_active: !m.is_active } })}
                      >
                        {m.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                      <Button
                        variant="quiet"
                        className="px-0"
                        disabled={change.isPending}
                        onClick={() => {
                          if (window.confirm(`Remove ${m.email} from this organisation? Their account stays but can see nothing until re-invited.`)) {
                            change.mutate({ id: m.id, patch: { org_id: null, is_active: false } })
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Invitations for this organisation
// ---------------------------------------------------------------------------
function InvitationsCard({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<AppRole>('org_admin')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)

  const { data: invitations } = usePendingInvitations(orgId)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['organisation', orgId, 'invitations'] })
    void queryClient.invalidateQueries({ queryKey: ['organisation', orgId, 'members'] })
    void queryClient.invalidateQueries({ queryKey: ['invitations', 'ktm'] })
  }

  const invite = useMutation({
    mutationFn: () => invokeFunction('invite-user', { email, fullName, orgId, role }),
    onSuccess: () => {
      setSent(email)
      setEmail('')
      setFullName('')
      setError(null)
      refresh()
    },
    onError: (e: Error) => {
      setError(e.message || 'That invitation could not be sent.')
      setSent(null)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    invite.mutate()
  }

  return (
    <Card className="mb-6 max-w-3xl">
      <h2 className="text-base text-ink">Invitations</h2>

      {(invitations ?? []).length === 0 ? (
        <p className="mt-1 text-sm text-inksoft">No invitations are waiting to be accepted.</p>
      ) : (
        <ul className="mt-3 divide-y divide-line text-sm">
          {invitations!.map((inv) => {
            const expired = new Date(inv.expires_at) < new Date()
            return (
              <li key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="text-ink">{inv.email}</span>
                <span className="text-inksoft">{ROLE_LABEL[inv.role]}</span>
                {expired ? (
                  <Badge tone="alert">Expired</Badge>
                ) : (
                  <span className="text-inksoft">expires {formatDate(inv.expires_at)}</span>
                )}
                <span className="ml-auto">
                  <InvitationActions
                    invitation={inv}
                    onChanged={() => {
                      setError(null)
                      refresh()
                    }}
                    onError={setError}
                  />
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <form onSubmit={submit} className="mt-4 grid gap-3 border-t border-line pt-4 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="font-medium text-ink">Full name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1 w-full rounded-sm border border-line px-3 py-2" />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Email address</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as AppRole)} className="mt-1 w-full rounded-sm border border-line px-3 py-2">
            {PARTNER_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </label>
        <div className="sm:col-span-3">
          {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
          {sent && <p className="mb-3 text-sm text-pounamu">Invitation sent to {sent}.</p>}
          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------
function DeleteCard({ org }: { org: OrganisationDetail }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const remove = useMutation({
    mutationFn: async () => {
      const typed = window.prompt(`This permanently deletes ${org.name}. Type its name to confirm.`)
      if (typed === null) return false
      if (typed.trim() !== org.name) throw new Error('The name did not match, so nothing was deleted.')
      const { error: deleteError } = await supabase.from('organisations').delete().eq('id', org.id)
      if (deleteError) {
        // Foreign keys are on delete restrict for people, submissions and
        // orders: a partner with any of those cannot be deleted by accident.
        if (deleteError.code === '23503') {
          throw new Error(
            'This organisation still has people, evaluation submissions or orders attached. ' +
              'Remove its people first. An organisation with submissions or orders cannot be ' +
              'deleted, because that data belongs to the partner; set its subscription to ' +
              'cancelled instead.',
          )
        }
        throw deleteError
      }
      return true
    },
    onSuccess: async (deleted) => {
      if (!deleted) return
      await queryClient.invalidateQueries({ queryKey: ['organisations'] })
      navigate('/admin/organisations')
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <Card className="max-w-3xl border-alert/40">
      <h2 className="text-base text-ink">Delete organisation</h2>
      <p className="mt-1 text-sm text-inksoft">
        For records created by mistake. A real partner that has stopped should be set to
        cancelled, which cuts access and keeps its history. Pending invitations are deleted with
        it.
      </p>
      {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}
      <button
        type="button"
        onClick={() => {
          setError(null)
          remove.mutate()
        }}
        disabled={remove.isPending}
        className="mt-3 text-sm text-alert underline underline-offset-4 hover:no-underline disabled:opacity-60"
      >
        {remove.isPending ? 'Deleting…' : 'Delete this organisation'}
      </button>
    </Card>
  )
}
